import { z } from "zod";

/**
 * What a price row multiplies: a kind of token, uses of a tool the vendor runs, the request itself,
 * or the dollars a router reported. Every cache read and write and every audio
 * token is also an input token, so the uncached meters count only what the cache meters leave over.
 * A cache write is metered by how long the vendor keeps it. A router's reported dollars are split
 * into what the input and the output cost when it says how its upstream priced each, and are one
 * `reportedCost` line when it doesn't.
 */
const pricedMeters = [
  "uncachedInput",
  "uncachedAudioInput",
  "implicitCacheRead",
  "explicitCacheRead",
  "cacheWrite5m",
  "cacheWrite30m",
  "cacheWrite1h",
  "cacheWrite12h",
  "output",
  "request",
  "reportedInputCost",
  "reportedOutputCost",
  "reportedCost",
] as const;

/**
 * Meters no row prices, because what was bought isn't known: `usage` is a call's whole usage when
 * the vendor reported none, and `cacheWriteUnknownLifetime` is cache writes whose lifetime nothing
 * pins down. A line on either makes the call's cost unknown.
 */
export const unknownMeters = ["usage", "cacheWriteUnknownLifetime"] as const;

type PricedMeter = (typeof pricedMeters)[number];
export type CacheWriteMeter = Extract<PricedMeter, `cacheWrite${string}`>;
type ToolMeter = `tool:${string}`;
export type Meter = PricedMeter | ToolMeter | (typeof unknownMeters)[number];

export const Meter = z.union([
  z.enum(pricedMeters),
  z.templateLiteral(["tool:", z.string()]),
  z.enum(unknownMeters),
]);

/** The quantity a row's amount is per. */
export type PriceUnit = "1M tokens" | "1k uses" | "request" | "USD";

/** Every price is in US dollars for now; the field names the currency so others can follow. */
type Currency = "USD";

/**
 * One price: an amount per unit of one meter, under the conditions given. A condition that is
 * absent holds for every call. For a meter, exactly one row of a SKU may hold for any call.
 */
export interface PriceRow {
  /** Stable within its SKU, such as `output` or `output.over-272k`; the full id is `<sku>#<id>`. */
  id: string;
  meter: Meter;
  unit: PriceUnit;
  currency: Currency;
  amount: number;
  /** UTC calendar dates, `YYYY-MM-DD`; `from` is the first day it holds and `until` the first it doesn't. */
  effective?: { from?: string; until?: string };
  /** UTC time of day, `HH:MM`, judged by when Pax received the call; it may wrap past midnight. */
  window?: { from: string; to: string };
  /**
   * The prompt's size, counted as the SKU says. `over` excludes its value, `upTo` includes it, and
   * either may be absent. The whole call is priced at the band its prompt falls in.
   */
  promptTokens?: { over?: number; upTo?: number };
}

/**
 * Something Pax can be billed for: one model, served on one endpoint at one service tier, billed
 * by one seller. Its id names all four, so a call priced at a SKU says who billed it and why. A
 * request alone decides its SKU. A router left to choose the endpoint is a SKU of its own, `auto`,
 * whose only meter is the cost the router reports.
 */
export interface Sku {
  /** `<billedBy>:<model>:<endpoint>:<tier>`. */
  id: string;
  /** Who invoices Pax, such as `anthropic`, `alibaba` or `openrouter`. */
  billedBy: string;
  /** The model as the seller names it, such as `claude-opus-5-5` or `openai/gpt-6-luna`. */
  model: string;
  /**
   * Where calls go and are billed from: a region such as `virginia`, an inference location such as
   * `us`, an OpenRouter endpoint such as `amazon-bedrock/us`, or `auto` when the router chooses.
   */
  endpoint: string;
  /** The service tier as the seller reports it, such as `standard`, `priority` or `ON_DEMAND`. */
  tier: string;
  /** Which tokens count toward `promptTokens` bands: every input token, cached or not. */
  promptTokensCount: "allInput";
  /** The most output tokens one call may ask for, when the seller limits it. */
  maxOutputTokens?: number;
  prices: readonly PriceRow[];
}

/** The facts that decide where a call goes and what it costs. */
export interface RoutingFacts {
  /** The model as asked for, or the dated version the response named. */
  model: string;
  /** Whose servers a router sent the call to, such as `amazon-bedrock`; `auto` when it chooses. */
  upstream: string;
  /** The service tier; `auto` when the seller chooses. */
  tier: string;
  /** Where the model ran. */
  inferenceLocation: string;
  /** Where the seller keeps the call's data, which can differ from where it ran. */
  dataLocation: string;
}

/**
 * What a call asked for and what its response reported, kept apart. A served fact that is absent
 * was not reported, not "the same as requested".
 */
export interface CallFacts {
  /** Who bills the call, known from its transport. */
  billedBy: string;
  requested: Partial<RoutingFacts>;
  served: Partial<RoutingFacts>;
}

/**
 * How a quantity is known: the vendor's response said so, Pax counted it with the model's
 * tokenizer, Pax estimated it, or nothing is known.
 */
const QuantitySource = z.enum(["reported", "counted", "estimated", "unknown"]);
export type QuantitySource = z.infer<typeof QuantitySource>;

/**
 * One meter of one call, priced at one row. Unknown is null, never zero. A line on an unknown
 * meter keeps its quantity but has no price.
 */
export const CostLine = z.object({
  meter: Meter,
  quantity: z.number().nonnegative().nullable(),
  quantitySource: QuantitySource,
  /** `<sku>#<row>`; null when no row prices the meter. */
  priceId: z.string().nullable(),
  unitPriceUsd: z.number().nonnegative().nullable(),
  listCostUsd: z.number().nonnegative().nullable(),
});
export type CostLine = z.infer<typeof CostLine>;

/**
 * A call's list cost. `listCostUsd` is the sum of its lines, or null when a line's quantity is
 * unknown or no row prices a line.
 */
export interface CallCost {
  /** Null when the request named no SKU Pax has. */
  sku: string | null;
  lines: CostLine[];
  listCostUsd: number | null;
  /** Lines whose quantity, or what they bought, is unknown. */
  unknownLines: number;
  /** Lines that have a quantity but no price: a gap in Pax's price data. */
  unpricedLines: number;
}

/**
 * What a prediction's cost figures rest on: estimated usage at the SKU's rows, estimated usage at
 * the effective prices per token the SKU's recent calls paid, or nothing.
 */
const PredictionBasis = z.enum(["rowsTimesPredictedUsage", "effectivePrices", "none"]);
type PredictionBasis = z.infer<typeof PredictionBasis>;

/** Which figure a call's hold reserved: `nothing` is a hold of zero, settled at what the call cost. */
export const ReservedFrom = z.enum(["p99", "ceiling", "nothing"]);
export type ReservedFrom = z.infer<typeof ReservedFrom>;

/**
 * What a call was expected to cost before it was sent. p50 and p99 are statistics: about one call
 * in a hundred costs more than its p99. The ceiling is the most it can cost, as far as its source
 * allows: a ceiling over an estimated prompt is a high guess, not a bound.
 */
export interface CostPrediction {
  basis: PredictionBasis;
  /** The calls of history the figures drew on; `0` when none did. */
  samples: number;
  /** Each meter at p50 and p99, when the basis predicts usage. */
  lines: (CostLine & { percentile: 50 | 99 })[];
  p50Usd: number | null;
  p99Usd: number | null;
  ceilingUsd: number | null;
  ceilingSource: QuantitySource | null;
}
